import SubscriptionBanner from '../subscription/SubscriptionBanner';
import InstallAppPrompt from '../InstallAppPrompt';
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Building2, Egg, Activity, ShoppingCart,
  UserCheck, Package, DollarSign, BarChart3, Bell, Bird,
  Menu, X, ChevronDown, LogOut, User, Lock,
  CreditCard, HelpCircle, CheckCircle2, Landmark
} from 'lucide-react';
import { useAuth, canManageUsers, canManageFarms, canViewFinancials, canManageSales, canManageInventory } from '../../context/AuthContext';
import { notificationService, birdsService } from '../../services/api';
import { clsx, timeAgo } from '../../utils/helpers';
import { Avatar, Modal, Button } from '../ui';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: { label: string; path: string }[];
  roles?: string[];
}

const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [howToOpen, setHowToOpen] = useState(false);
  const [checkpointQueue, setCheckpointQueue] = useState<any[]>([]);

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const currentCheckpoint = checkpointQueue[0] || null;

  const howToSteps = [
    'Go to Farm Management to add your farm.',
    'Go to Egg Pricing to set pricing for each egg size.',
    'Go to Birds Management and add your total number of birds.',
    'Go to User Management and add your workers by email. Set a temporary password they can change later.',
    'Add your inventory.',
    'Go to Farm Activities and log farm activities.',
    'Go to Egg Collection and record eggs collected. You can record them as unsorted and sort them later.',
    'Record sales when eggs are sold.',
    'Record expenses.',
    'Repeat the process as your farm operations continue.',
  ];

  useEffect(() => {
    notificationService.getAll().then(r => setNotifications(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!canManageInventory(user!.role)) return;
    birdsService.getDueHealthCheckpoints().then(r => {
      const due = r.data.data || [];
      if (due.length === 0) return;
      setCheckpointQueue(due);
      setNotifications(prev => [...due, ...prev]);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissCheckpoint = () => {
    const item = checkpointQueue[0];
    if (!item) return;
    notificationService.markRead([item.id]).catch(() => {});
    setNotifications(prev => prev.map(n => (n.id === item.id ? { ...n, is_read: true } : n)));
    setCheckpointQueue(prev => prev.slice(1));
  };

  const navItems: NavItem[] = [
    { label: 'Dashboard', icon: <LayoutDashboard size={18} />, path: '/dashboard' },
    ...(canManageUsers(user!.role) ? [
      { label: 'User Management', icon: <Users size={18} />, path: '/users' },
    ] : []),
    ...(canManageFarms(user!.role) ? [
      { label: 'Farm Management', icon: <Building2 size={18} />, path: '/farms' },
    ] : []),
    ...(user!.role === 'super_admin' ? [
      { label: 'Egg Pricing', icon: <Egg size={18} />, path: '/settings/egg-pricing' },
    ] : []),
    ...(user!.role !== 'sales_officer' ? [
      { label: 'Egg Collection', icon: <Egg size={18} />, path: '/eggs' },
    ] : []),
    { label: 'Farm Activities', icon: <Activity size={18} />, path: '/activities' },
    ...(canManageSales(user!.role) ? [
      {
        label: 'Sales', icon: <ShoppingCart size={18} />,
        children: [
          { label: 'All Sales', path: '/sales' },
          { label: 'New Sale', path: '/sales/new' },
        ]
      },
      { label: 'Customers', icon: <UserCheck size={18} />, path: '/customers' },
      { label: 'Receivables', icon: <CreditCard size={18} />, path: '/receivables' },
    ] : []),
    ...(user!.role !== 'sales_officer' ? [
      { label: 'Birds Management', icon: <Bird size={18} />, path: '/birds' },
    ] : []),
    { label: 'Inventory', icon: <Package size={18} />, path: '/inventory' },
    ...(canViewFinancials(user!.role) ? [
      { label: 'Expenses', icon: <DollarSign size={18} />, path: '/expenses' },
      { label: 'Creditors', icon: <Landmark size={18} />, path: '/creditors' },
      { label: 'Profit & Loss', icon: <BarChart3 size={18} />, path: '/profit-loss' },
    ] : []),
    ...(user!.role === 'super_admin' ? [
      { label: 'Subscription', icon: <CreditCard size={18} />, path: '/subscription' },
    ] : []),
  ];

  const isActive = (path?: string) => path && location.pathname.startsWith(path) && path !== '/dashboard'
    ? true
    : path === '/dashboard' && location.pathname === '/dashboard';

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-4 py-4 border-b border-gray-100">
        <Link to="/dashboard" onClick={() => setSidebarOpen(false)}>
          <img src="/poultrypro-logo-horizontal.svg" alt="PoultryPro" className="h-14 w-auto" style={{ maxWidth: '200px' }} />
        </Link>
      </div>

      {user?.farms && (
        <div className="px-5 py-3 bg-green-50/60 border-b border-green-100/80">
          <p className="text-xs text-green-700 font-medium truncate">{user.farms.name}</p>
          <p className="text-xs text-green-500">{user.farms.code}</p>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        {navItems.map((item) => {
          if (item.children) {
            const groupActive = item.children.some(c => location.pathname.startsWith(c.path));
            const expanded = expandedGroup === item.label || groupActive;
            return (
              <div key={item.label}>
                <button
                  onClick={() => setExpandedGroup(expanded ? null : item.label)}
                  className={clsx('w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                    groupActive ? 'text-green-700 bg-green-50' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  )}>
                  {item.icon}
                  <span className="flex-1 text-left">{item.label}</span>
                  <ChevronDown size={14} className={clsx('transition-transform', expanded ? 'rotate-180' : '')} />
                </button>
                {expanded && (
                  <div className="ml-9 mt-0.5 space-y-0.5">
                    {item.children.map(child => (
                      <Link key={child.path} to={child.path} onClick={() => setSidebarOpen(false)}
                        className={clsx('block px-3 py-2 rounded-lg text-xs font-medium transition-all',
                          location.pathname === child.path ? 'bg-green-600 text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                        )}>
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <Link key={item.path} to={item.path!} onClick={() => setSidebarOpen(false)}
              className={isActive(item.path) ? 'sidebar-link-active' : 'sidebar-link'}>
              {item.icon}
              {item.label}
            </Link>
          );
        })}

        <button
          type="button"
          onClick={() => { setHowToOpen(true); setSidebarOpen(false); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-gray-600 hover:bg-green-50 hover:text-green-700"
        >
          <HelpCircle size={18} />
          <span>How to Use</span>
        </button>
      </nav>

      <div className="px-3 py-3 border-t border-gray-100">
        <Link to="/settings/profile" className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
          <Avatar name={`${user?.first_name} ${user?.last_name}`} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-800 truncate">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-gray-400 capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
        </Link>
        <button onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-50 transition-colors mt-1">
          <LogOut size={15} />
          <span className="text-xs font-medium">Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <InstallAppPrompt />

      <aside className="hidden lg:flex flex-col w-60 bg-white border-r border-gray-100 shrink-0">
        <SidebarContent />
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white shadow-xl z-50">
            <SidebarContent />
          </aside>
        </div>
      )}


      {howToOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6">
          <div className="absolute inset-0 bg-black/40" onClick={() => setHowToOpen(false)} />
          <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100">
            <div className="sticky top-0 bg-white px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <p className="text-lg font-bold text-gray-900">How to Use PoultryPro</p>
                <p className="text-xs text-gray-500">Follow these steps to run your farm records correctly.</p>
              </div>
              <button onClick={() => setHowToOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {howToSteps.map((step, index) => (
                <div key={step} className="flex gap-3 rounded-xl border border-gray-100 bg-gray-50/70 p-3">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 size={15} className="text-green-600 mt-0.5 shrink-0" />
                      <p className="text-sm text-gray-700 leading-relaxed">{step}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {currentCheckpoint && (
        <Modal open={!!currentCheckpoint} onClose={dismissCheckpoint} title="🐔 Flock Health Checkpoint" size="md"
          footer={
            <>
              <Link to="/birds/health-chart" onClick={dismissCheckpoint}>
                <Button variant="secondary">View Full Chart</Button>
              </Link>
              <Button onClick={dismissCheckpoint}>Got it</Button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="p-3 rounded-xl bg-gray-50">
              <p className="text-sm font-semibold text-gray-900">{currentCheckpoint.flock_name}</p>
              <p className="text-xs text-gray-500">{currentCheckpoint.age_label}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Vaccination & Health</p>
              <p className="text-sm text-gray-700 leading-relaxed">{currentCheckpoint.vaccination}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Feed</p>
              <p className="text-sm text-gray-700 leading-relaxed">{currentCheckpoint.feed}</p>
            </div>
          </div>
        </Modal>
      )}

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="bg-white border-b border-gray-100 px-4 lg:px-6 py-3.5 flex items-center gap-4 shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-500">
            <Menu size={20} />
          </button>

          <Link to="/dashboard" className="lg:hidden flex items-center gap-2" onClick={() => setSidebarOpen(false)}>
            <img src="/poultrypro-logo.svg" alt="PoultryPro" className="h-8 w-8" />
            <span className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
              Poultry<span className="text-green-600 italic font-normal">Pro</span>
            </span>
          </Link>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 hidden lg:block truncate">
              {navItems.find(n => n.path && location.pathname.startsWith(n.path))?.label || 'Dashboard'}
            </p>
          </div>

          <div className="relative">
            <button onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }}
              className="relative p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-11 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-30 max-h-80 overflow-y-auto">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-800">Notifications</p>
                  <button onClick={() => setNotifOpen(false)}><X size={14} className="text-gray-400" /></button>
                </div>
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-xs text-gray-400">No notifications</div>
                ) : notifications.slice(0, 10).map(n => (
                  <div key={n.id} className={clsx('px-4 py-3 border-b border-gray-50 hover:bg-gray-50', !n.is_read && 'bg-green-50/40')}>
                    <p className="text-xs font-semibold text-gray-800">{n.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{n.message}</p>
                    <p className="text-xs text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }}
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-gray-50 transition-colors">
              <Avatar name={`${user?.first_name} ${user?.last_name}`} size="sm" />
              <span className="hidden sm:block text-xs font-medium text-gray-700 max-w-24 truncate">{user?.first_name}</span>
              <ChevronDown size={14} className="text-gray-400" />
            </button>
            {profileOpen && (
              <div className="absolute right-0 top-11 w-52 bg-white rounded-xl shadow-xl border border-gray-100 z-30 py-1">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-800">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-gray-400 capitalize">{user?.role?.replace(/_/g, ' ')}</p>
                </div>
                <Link to="/settings/profile" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-xs text-gray-700 hover:bg-gray-50">
                  <User size={14} /> My Profile
                </Link>
                <Link to="/settings/password" onClick={() => setProfileOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-xs text-gray-700 hover:bg-gray-50">
                  <Lock size={14} /> Change Password
                </Link>
                <div className="border-t border-gray-100 mt-1">
                  <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-red-500 hover:bg-red-50">
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        <SubscriptionBanner />

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;